import { useState } from "react";
import { OperatingFrontChrome } from "@/components/operating-front/OperatingFrontScreens";
import { SalesDeskConfigure } from "./SalesDeskConfigure";
import { SalesDeskHome } from "./SalesDeskHome";
import { SalesDeskLeadPanel } from "./SalesDeskLeadPanel";
import { SalesDeskLot } from "./salesDeskLot";
import { SalesDeskMatches } from "./SalesDeskMatches.tsx";
import { SalesDeskOrders } from "./salesDeskOrders";
import { SalesDeskPerformance } from "./SalesDeskPerformance";
import { SalesDeskRecord } from "./SalesDeskRecord.tsx";
import { SalesDeskRestock } from "./SalesDeskRestock";
import { SalesDeskStockForm } from "./SalesDeskStockForm.tsx";
import { SalesDeskNav } from "./SalesDeskNav";
import { SalesDeskStart } from "./SalesDeskPages";
import type { SalesDeskPage } from "./salesDeskStyles";
import { salesDeskHandoffEntry, salesDeskSameModelSales, type SalesDeskHistoryEntry } from "./salesDeskSaleRecord";
import {
  salesDeskStockDestination,
  salesDeskStockDraftFromSale,
  submitSalesDeskStockDraft,
  type SalesDeskStockDraft,
  type SalesDeskStockRequest,
} from "./salesDeskStockRequest";
import type { SalesDeskHomeRowCount } from "./salesDeskHomeLayout";
import { recordSalesDeskShown, type SalesDeskShownEvent } from "./salesDeskLotFilter";
import { salesDeskReceiptsNeedingReview } from "./salesDeskQuoteMatch";
import { useSalesDeskConfigure } from "./useSalesDeskConfigure";
import type { SalesDeskLead } from "./salesDeskLead";
import { useSalesDeskHome } from "./useSalesDeskHome";
import { useSalesDeskOrders } from "./useSalesDeskOrders";

export function SalesDesk() {
  const [page, setPage] = useState<SalesDeskPage>("home");
  const [saleId, setSaleId] = useState<string | null>(null);
  const [stockDraft, setStockDraft] = useState<SalesDeskStockDraft | null>(null);
  const [stockRequests, setStockRequests] = useState<SalesDeskStockRequest[]>([]);
  const [handoffNotes, setHandoffNotes] = useState<Record<string, SalesDeskHistoryEntry[]>>({});
  const [showLeads, setShowLeads] = useState(true);
  const [showBuilds, setShowBuilds] = useState(true);
  const [homeRows, setHomeRows] = useState<SalesDeskHomeRowCount>(3);
  const [shownEvents, setShownEvents] = useState<SalesDeskShownEvent[]>([]);
  const [saleLead, setSaleLead] = useState<SalesDeskLead | null>(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const home = useSalesDeskHome();
  const loaded = useSalesDeskOrders();
  const receipts = useSalesDeskConfigure();
  const sale = loaded.orders.find((order) => order.id === saleId) ?? null;

  function openSale(id: string) {
    setSaleId(id);
    setPage("record");
  }

  function openStockForm(evidenceIds: string[]) {
    if (!sale) return;
    setStockDraft(
      salesDeskStockDraftFromSale(
        sale,
        salesDeskStockDestination(home.locations, home.activeLocationId, home.locationLabel),
        evidenceIds,
      ),
    );
    setPage("stockForm");
  }

  function orderAnotherFromRecord() {
    if (!sale) return;
    const evidenceIds = salesDeskSameModelSales(loaded.orders, sale.id, sale.model, 90, Date.now()).map((row) => row.id);
    openStockForm(evidenceIds);
  }

  return (
    <OperatingFrontChrome showSwitch>
      <div className="pb-24">
        {page === "home" ? (
          <SalesDeskHome
            onStart={() => {
              setSaleLead(null);
              setLeadOpen(false);
              setPage("start");
            }}
            onOpenLead={(lead) => {
              setSaleLead(lead);
              setLeadOpen(true);
            }}
            onConfigure={() => setPage("configure")}
            onLot={() => setPage("lot")}
            onOrders={() => setPage("orders")}
            onOpenSale={openSale}
            onReceipts={() => setPage("configure")}
            locationLabel={home.locationLabel}
            leads={home.leads}
            leadsLoading={home.leadsLoading}
            orders={loaded.orders}
            ordersLoading={loaded.loading}
            receiptReviewCount={salesDeskReceiptsNeedingReview(receipts.quotes.map((quote) => quote.match))}
            receiptsLoading={receipts.loading}
            showLeads={showLeads}
            showBuilds={showBuilds}
            rowCount={homeRows}
            onShowLeads={setShowLeads}
            onShowBuilds={setShowBuilds}
            onRowCount={setHomeRows}
          />
        ) : null}
        {page === "start" ? (
          <SalesDeskStart lead={saleLead} onLot={() => setPage("lot")} onConfigure={() => setPage("configure")} />
        ) : null}
        {page === "lot" ? (
          <SalesDeskLot
            locationLabel={home.locationLabel}
            onOpenSale={openSale}
            shownEvents={shownEvents}
            onShown={(unitId) => setShownEvents((events) => recordSalesDeskShown(events, unitId, new Date().toISOString()))}
          />
        ) : null}
        {page === "orders" ? <SalesDeskOrders loaded={loaded} onOpen={openSale} /> : null}
        {page === "record" ? (
          <SalesDeskRecord
            sale={sale}
            notes={saleId ? (handoffNotes[saleId] ?? []) : []}
            onMatches={() => setPage("matches")}
            onOrderAnother={orderAnotherFromRecord}
            onHandoff={(action) => {
              if (!saleId) return;
              const entry = salesDeskHandoffEntry(action, new Date().toISOString().slice(0, 10));
              setHandoffNotes((current) => ({ ...current, [saleId]: [entry, ...(current[saleId] ?? [])] }));
            }}
          />
        ) : null}
        {page === "matches" ? (
          <SalesDeskMatches sale={sale} sales={loaded.orders} nowMs={Date.now()} onOrderAnother={openStockForm} />
        ) : null}
        {page === "stockForm" && stockDraft ? (
          <SalesDeskStockForm
            draft={stockDraft}
            locations={home.locations}
            onDraftChange={setStockDraft}
            onBack={() => setPage("record")}
            onSubmit={(attachEvidence) => {
              const result = submitSalesDeskStockDraft(stockDraft, stockRequests, attachEvidence);
              if (!result.ok) return "Name the lot this building is for.";
              setStockRequests((rows) => [...rows, result.request]);
              setStockDraft(null);
              setPage("restock");
              return null;
            }}
          />
        ) : null}
        {page === "configure" ? <SalesDeskConfigure onBack={() => setPage("start")} receipts={receipts} /> : null}
        {page === "performance" ? (
          <SalesDeskPerformance stockRequests={stockRequests.length} shownToCustomer={shownEvents.length} />
        ) : null}
        {page === "restock" ? <SalesDeskRestock requests={stockRequests} onOpenSale={openSale} /> : null}
      </div>
      {page === "home" && leadOpen && saleLead ? (
        <SalesDeskLeadPanel
          lead={saleLead}
          onClose={() => {
            setLeadOpen(false);
            setSaleLead(null);
          }}
          onStart={() => {
            setLeadOpen(false);
            setPage("start");
          }}
        />
      ) : null}
      <SalesDeskNav page={page === "record" || page === "matches" ? "orders" : page} onChange={setPage} />
    </OperatingFrontChrome>
  );
}
